import { request } from './http';
import type {
  ChildListResult,
  ChildSummary,
  InvitationItem,
  InvitationListResult,
  ParentWalletTransfer,
  ParentWalletTransferListResult,
  TransferDirection,
} from '@/types/team';

export const teamApi = {
  listChildren(params?: { keyword?: string; page?: number; pageSize?: number }) {
    return request<ChildListResult>({ url: '/team/children', method: 'GET', params });
  },

  updateChild(childUserId: string, payload: { enabled: boolean }, stepUpToken: string) {
    return request<ChildSummary>({
      url: `/team/children/${childUserId}`,
      method: 'PATCH',
      data: payload,
      headers: { 'X-Step-Up-Token': stepUpToken },
    });
  },

  updateChildFees(
    childUserId: string,
    payload: { depositFeeRateBps?: number; cardOpenFeeCents?: number },
    stepUpToken: string,
  ) {
    return request<{ userId: string; depositFeeRateBps: number; cardOpenFeeCents: number; feeUpdatedAt?: string }>({
      url: `/team/children/${childUserId}/fees`,
      method: 'PATCH',
      data: payload,
      headers: { 'X-Step-Up-Token': stepUpToken },
    });
  },

  listInvitations() {
    return request<InvitationListResult>({ url: '/team/invitations', method: 'GET' });
  },

  createInvitation(payload: {
    label?: string;
    maxUses?: number;
    expiresAt?: string;
    depositFeeRateBps: number;
    cardOpenFeeCents: number;
  }) {
    return request<InvitationItem>({ url: '/team/invitations', method: 'POST', data: payload });
  },

  updateInvitation(
    id: string,
    payload: {
      enabled?: boolean;
      depositFeeRateBps?: number;
      cardOpenFeeCents?: number;
    },
  ) {
    return request<InvitationItem>({ url: `/team/invitations/${id}`, method: 'PATCH', data: payload });
  },

  disableInvitation(id: string) {
    return request<InvitationItem>({
      url: `/team/invitations/${id}`,
      method: 'PATCH',
      data: { enabled: false },
    });
  },

  createWalletTransfer(payload: {
    childUserId: string;
    direction: TransferDirection;
    amountCents: number;
    reason: string;
    stepUpToken: string;
  }) {
    const { stepUpToken, ...data } = payload;
    return request<ParentWalletTransfer>({
      url: '/team/wallet-transfers',
      method: 'POST',
      data,
      headers: { 'X-Step-Up-Token': stepUpToken },
    });
  },

  listWalletTransfers(params?: {
    childUserId?: string;
    direction?: TransferDirection;
    page?: number;
    pageSize?: number;
  }) {
    return request<ParentWalletTransferListResult>({
      url: '/team/wallet-transfers',
      method: 'GET',
      params,
    });
  },
};

export const publicApi = {
  validateInvitation(code: string) {
    return request<import('@/types/user').InvitationValidateResult>({
      url: `/public/invitations/${encodeURIComponent(code)}`,
      method: 'GET',
    });
  },
};
